
import React, { useState, useCallback, memo } from "react";
import { useAuth } from "@/components/AuthProvider";
import { useNavigate } from "react-router-dom";
import { useUpdateManager } from "@/hooks/useUpdateManager";
import { toast } from "sonner";
import { safeReload } from "@/utils/navigation";
import { useRecentAchievements } from "@/hooks/useRecentAchievements";
import { NavButtons } from "./navigation/NavButtons";
import { StatusSection } from "./navigation/StatusSection";
import { QuickActionButtons } from "./navigation/QuickActionButtons";
import { AchievementNotificationCenter } from "@/components/achievements/AchievementNotificationCenter";
import { ReliableComponentBoundary } from "@/components/reliability/ReliableComponentBoundary";

const NavigationBarContent = memo(() => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  const { checkForUpdates } = useUpdateManager();
  
  // Use lightweight hook - no AnalogHorror dependency
  const { data: recentAchievements = [] } = useRecentAchievements(user?.id);

  // Memoize navigation handler
  const handleNavigate = useCallback((path: string) => {
    navigate(path);
  }, [navigate]);

  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    
    try {
      const hasUpdate = await checkForUpdates();
      
      if (hasUpdate) {
        toast.success("Update available! You can install it from the notification.");
      } else {
        toast.info("App is up to date - refreshing page...");
        setTimeout(() => {
          safeReload('nav-refresh');
        }, 1000);
      }
    } catch (error) {
      toast.error("Failed to check for updates - refreshing page...");
      setTimeout(() => {
        safeReload('nav-refresh');
      }, 1000);
    } finally {
      setIsRefreshing(false);
    }
  }, [checkForUpdates]);

  if (!user) return null;

  return (
    <nav className="bg-win95-gray border-t-2 border-r-2 border-white border-b-2 border-l-2 border-black p-1 overflow-hidden">
      <div className="flex flex-nowrap items-center space-x-1">
        <NavButtons onNavigate={handleNavigate} />
        
        <StatusSection />
        
        {/* Achievement Notifications - with error boundary */}
        <ReliableComponentBoundary componentName="Achievements">
          <AchievementNotificationCenter recentAchievements={recentAchievements} />
        </ReliableComponentBoundary>
        
        <QuickActionButtons 
          onNavigate={handleNavigate}
          onRefresh={handleRefresh}
          isRefreshing={isRefreshing}
        />
      </div>
    </nav>
  );
});

NavigationBarContent.displayName = 'NavigationBarContent';

// Wrap entire NavigationBar in error boundary
export const NavigationBar = () => (
  <ReliableComponentBoundary 
    componentName="NavigationBar"
    fallback={
      <nav className="bg-win95-gray border-t-2 border-r-2 border-white border-b-2 border-l-2 border-black p-1">
        <div className="text-sm text-gray-600 px-2">Navigation loading...</div>
      </nav>
    }
  >
    <NavigationBarContent />
  </ReliableComponentBoundary>
);
